/**
 * Acknowledge Flagged Break Modal (PR 1, Phase B1 · BREAK-ACK-NOTE-001)
 *
 * Admin/TL action — mark a flagged break as reviewed. The acknowledgement
 * note is MANDATORY (non-blank, ≤ 500 chars) and write-once: for already
 * acknowledged breaks the modal renders a read-only audit view instead.
 *
 * Backend contract: POST /api/breaks/{id}/acknowledge
 *   Body: { note: string }  → 409 when already acknowledged
 *   Response: { ok: true, break: <updated_row> }
 */

import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { CheckCircle2, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTimeShortET } from './breakUiHelpers';

const API = process.env.REACT_APP_BACKEND_URL || '';
const MAX_NOTE = 500;

const AckAuditView = ({ breakRecord }) => {
  const ack = breakRecord.admin_acknowledgement || {};
  const rows = [
    ['Acknowledged by', `${ack.acknowledged_by_name || breakRecord.admin_acknowledged_by_name || '—'}${ack.acknowledged_by_role ? ` (${String(ack.acknowledged_by_role).replace(/_/g, ' ')})` : ''}`],
    ['Acknowledged at', formatDateTimeShortET(ack.acknowledged_at || breakRecord.admin_acknowledged_at)],
    ['Employee', ack.employee_name || breakRecord.user_name || '—'],
    ['Break', `${ack.break_type_name || breakRecord.break_type_name || '—'} · ${formatDateTimeShortET(ack.start_time || breakRecord.start_time)}`],
    ['Duration', `${ack.duration_minutes ?? breakRecord.duration_minutes ?? '—'} min${(ack.max_duration_minutes ?? breakRecord.max_duration_minutes) ? ` / ${ack.max_duration_minutes ?? breakRecord.max_duration_minutes} min cap` : ''}`],
    ['Original flag reason', ack.flagged_reason || breakRecord.flagged_reason || '—'],
  ];
  return (
    <div className="py-1 space-y-3" data-testid="ack-audit-view">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm">
        {rows.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="text-slate-500">{k}</dt>
            <dd className="text-slate-800 break-words" data-testid={`ack-audit-${k.toLowerCase().replace(/\s+/g, '-')}`}>{v}</dd>
          </React.Fragment>
        ))}
      </dl>
      <div>
        <div className="text-xs font-semibold text-slate-600 mb-1">Acknowledgement Note</div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 whitespace-pre-wrap" data-testid="ack-audit-note">
          {ack.note || breakRecord.admin_note || '—'}
        </div>
        <p className="flex items-center gap-1 text-[11px] text-slate-400 mt-1.5" data-testid="ack-audit-locked">
          <Lock className="w-3 h-3" /> Acknowledgement notes are write-once and cannot be edited.
        </p>
      </div>
    </div>
  );
};

export const AcknowledgeBreakModal = ({ open, onOpenChange, breakRecord, onSuccess }) => {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => { setNote(''); }, [breakRecord]);

  const trimmed = note.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= MAX_NOTE && !submitting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API}/api/breaks/${breakRecord.id}/acknowledge`,
        { note: trimmed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Break acknowledged.', {
        description: `Reviewed by ${resp.data.break?.admin_acknowledged_by_name || 'you'}`,
      });
      onOpenChange(false);
      onSuccess?.(resp.data.break);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error('Failed to acknowledge break', {
        description: typeof detail === 'string' ? detail : (detail?.[0]?.msg || err?.message),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!breakRecord) return null;

  const isAcked = !!breakRecord.admin_acknowledged;

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent className="max-w-md" data-testid="ack-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            {isAcked ? 'Acknowledgement Details' : 'Acknowledge Flagged Break'}
          </DialogTitle>
          <DialogDescription>
            <strong>{breakRecord.user_name}</strong> — <em>{breakRecord.break_type_name}</em>
            {' '}({breakRecord.duration_minutes ?? '—'} min).
            {breakRecord.flagged_reason && (
              <span className="block mt-1 text-rose-700">
                Flag reason: {breakRecord.flagged_reason}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {isAcked ? (
          <AckAuditView breakRecord={breakRecord} />
        ) : (
          <div className="py-2">
            <Label htmlFor="ack-note" className="text-sm">
              Acknowledgement Note <span className="text-rose-600" aria-hidden="true">*</span>
            </Label>
            <Textarea
              id="ack-note"
              data-testid="ack-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Confirmed with employee — extended due to client call"
              maxLength={MAX_NOTE}
              rows={3}
              className="mt-1"
              disabled={submitting}
              required
              aria-required="true"
            />
            <p className="text-xs text-gray-400 mt-1" data-testid="ack-note-hint">
              Required — document why this exception is accepted. Stored with your name and timestamp for audit. {note.length}/{MAX_NOTE}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="ack-cancel">
            {isAcked ? 'Close' : 'Cancel'}
          </Button>
          {!isAcked && (
            <Button
              onClick={handleConfirm}
              disabled={!canSubmit}
              title={canSubmit ? undefined : 'Enter an acknowledgement note to continue'}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="ack-confirm"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : 'Acknowledge'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AcknowledgeBreakModal;
